<script setup>
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { reactive, ref } from 'vue';
import { object, string } from 'yup';

const { tenant, websiteVariants, seoBacklinkPlatforms, callback } = defineProps(
    {
        tenant: {
            type: Object,
            required: true,
        },
        websiteVariants: {
            type: Array,
            required: true,
        },
        seoBacklinkPlatforms: {
            type: Array,
            required: true,
        },
        callback: {
            type: Function,
            required: false,
        },
    },
);

const { route } = useRoute();

const form = reactive({
    websiteVariant: null,
    platform: seoBacklinkPlatforms[0],
    sourceUrls: '',
    date: '',
});

const validationSchema = object({
    websiteVariant: object().required('Ce champ est obligatoire.'),
    platform: object().required('Ce champ est obligatoire.'),
    sourceUrls: string().required('Ce champ est obligatoire.'),
    date: string().nullable(),
});

const isSubmitting = ref(false);

const isOpen = ref(false);

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.seo.postImportBacklinks', {
                tenant: tenant.id,
            }),
            {
                websiteVariantId: form.websiteVariant?.id,
                platform: form.platform.value,
                sourceUrls: form.sourceUrls
                    .split('\n')
                    .filter((value) => value.trim() !== ''),
                date: form.date,
            },
        )
        .then(() => {
            isOpen.value = false;

            if (callback) {
                callback();
            }
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <DialogDrawer v-model="isOpen" size="extra-large">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Importer des backlinks </template>

        <template #full-content>
            <FormRoot
                :validation-schema="validationSchema"
                :submit="submit"
                v-slot="{ errors }"
            >
                <FormColumn>
                    <FormContent>
                        <ComboboxField
                            filtered-option-key="name"
                            :options="websiteVariants"
                            field-name="websiteVariant"
                            field-id="websiteVariant"
                            label="Site"
                            :model-value="form.websiteVariant"
                            @update:modelValue="
                                (value) => (form.websiteVariant = value)
                            "
                            :required="true"
                        >
                            <template #optionContent="{ option }">
                                {{ option.name }}
                            </template>
                        </ComboboxField>

                        <ListboxField
                            v-model="form.platform"
                            field-id="platform"
                            field-name="platform"
                            label="Plateforme"
                            :options="seoBacklinkPlatforms"
                            required
                        >
                            <template #buttonContent>
                                {{ form.platform.label }}
                            </template>

                            <template #optionContent="{ option }">
                                {{ option.label }}
                            </template>
                        </ListboxField>
                    </FormContent>

                    <FormContent>
                        <TextareaField
                            v-model="form.sourceUrls"
                            label="URLs source"
                            placeholder="URLs source"
                            field-name="sourceUrls"
                            field-id="sourceUrls"
                            legend="Une URL source par ligne"
                            required
                        />
                    </FormContent>

                    <FormContent>
                        <InputField
                            v-model="form.date"
                            label="Date"
                            placeholder="Date"
                            field-name="date"
                            field-id="date"
                            type="month"
                        />
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    submit-text="Importer"
                    submitting-text="Import en cours..."
                    without-back-button
                />
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
